import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import { StyledMultiSelect } from "../../customComponents/styled/customMultiSelect";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { useNotifyInvoicesBulkMutation } from "./invoicesApiSlice";
import toast from "../../../helpers/toast";

function NotifyParentsDialog({ open, setOpen }) {
  const [allClasses, setAllClasses] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [result, setResult] = useState(null);

  const { data: classroomsData } = useGetClassesPaginatedQuery({
    page: 1,
    per_page: 100,
  });
  const classrooms = classroomsData?.classrooms ?? [];

  const [notifyBulk, { isLoading }] = useNotifyInvoicesBulkMutation();

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setAllClasses(true);
    setSelectedClasses([]);
  };

  const handleSubmit = async () => {
    if (!allClasses && selectedClasses.length === 0) {
      toast.error("Select at least one class, or choose \"All classes\"");
      return;
    }
    try {
      const response = await notifyBulk({
        classroom_ids: allClasses ? [] : selectedClasses.map((c) => c.value),
      }).unwrap();
      setResult(response);
      if (response.notified > 0) {
        toast.success(`Notified parents for ${response.notified} invoice(s)`);
      } else {
        toast.info("No un-notified invoices found for that selection");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to notify parents");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6">Notify Parents</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseSharpIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sends a notification only for invoices that haven't been notified yet —
          already-notified invoices are skipped automatically, so this is safe
          to run repeatedly as new invoices are generated.
        </Typography>

        <Grid container rowSpacing={2.5}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allClasses}
                  onChange={(e) => {
                    setAllClasses(e.target.checked);
                    setResult(null);
                  }}
                />
              }
              label="All classes"
            />
          </Grid>

          {!allClasses && (
            <Grid item xs={12}>
              <Typography variant="formSubHeading">CLASSES</Typography>
              <FormControl fullWidth>
                <StyledMultiSelect
                  value={selectedClasses}
                  onChange={(selected) => {
                    setSelectedClasses(selected);
                    setResult(null);
                  }}
                  options={classrooms.map((c) => ({ label: c.name, value: c.id }))}
                  placeholder="Select one or more classes"
                />
              </FormControl>
            </Grid>
          )}

          {result && (
            <Grid item xs={12}>
              <Alert severity={result.notified > 0 ? "success" : "info"}>
                {result.matched} un-notified invoice(s) matched — notified {result.notified}
                {result.failed?.length > 0 ? `, ${result.failed.length} failed (no linked student)` : ""}.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <StyledLoadingButton variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>
          Close
        </StyledLoadingButton>
        <StyledLoadingButton
          loading={isLoading}
          onClick={handleSubmit}
          sx={{ textTransform: "none" }}
        >
          Notify Parents
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default NotifyParentsDialog;
